import React from 'react';

// ---------------------------------------------------------------------------
// Route-level lazy loading — each page becomes a separate async webpack chunk
// that is only downloaded when the user navigates to that route.
// ---------------------------------------------------------------------------

export const DashboardPageV2 = React.lazy(
	() => import('features/Dashboard/DashboardPageV2'),
);
export const FavoritesPage = React.lazy(() =>
	import('features/Favorites/FavoritesPage').then(m => ({
		default: m.FavoritesPage,
	})),
);
export const RecentsPage = React.lazy(() =>
	import('features/Recents/RecentsPage').then(m => ({default: m.RecentsPage})),
);
export const SubscriptionsPage = React.lazy(
	() => import('features/Subscriptions/SubscriptionsPage'),
);
export const RegulationsPage = React.lazy(
	() => import('features/Regulations/RegulationsPage'),
);
export const RegulationDetail = React.lazy(
	() => import('features/Regulations/RegulationDetail/RegulationDetail'),
);
export const RegulatoryDocumentDetailsPage = React.lazy(
	() =>
		import(
			'features/RegulatoryDocuments/RegDocDetailsPage/RegDocDetailsPage'
		),
);
export const RegulatoryDocumentEditorPage = React.lazy(
	() => import('features/RegulatoryDocuments/RegulatoryDocumentEditorPage'),
);
export const ParagraphDetailsPage = React.lazy(
	() =>
		import(
			'features/RegulatoryDocuments/components/DocumentDetails/ParagraphDetails/ParagraphDetailsPage'
		),
);
export const QuestionPage = React.lazy(() =>
	import('components/Faq/QuestionPage').then(m => ({default: m.QuestionPage})),
);
export const RegulatoryDocumentArchivePage = React.lazy(
	() => import('features/Archive/RegulatoryDocumentArchivePage'),
);
export const PogisDocumentPage = React.lazy(
	() => import('features/Archive/PogisDocumentPage'),
);
export const RequirementsPage = React.lazy(
	() => import('features/Requirements/RequirementsPage'),
);
export const RequirementDetailsPage = React.lazy(
	() =>
		import(
			'features/Requirements/RequirementDetailsPage/RequirementDetailsPage'
		),
);
export const VehicleProjectsPage = React.lazy(
	() =>
		import(
			'features/VehicleProjects/VehicleProjectOverview/VehicleProjectsPage'
		),
);
export const VehicleProjectDetailsPage = React.lazy(
	() =>
		import(
			'features/VehicleProjects/VehicleProjectDetails/vehicleProjectDetailsPage'
		),
);
export const VkoVexListPage = React.lazy(
	() => import('features/VkoVexList/VkoVexListPage'),
);
export const BaselineListPage = React.lazy(() =>
	import('features/Baselines/BaselineListPage/BaselineListPage').then(m => ({
		default: m.BaselineListPage,
	})),
);
export const BaselineDetailsPage = React.lazy(() =>
	import('features/Baselines/BaselineDetailsPage/BaselineDetailsPage').then(
		m => ({default: m.BaselineDetailsPage}),
	),
);
export const BaselineComparisonPage = React.lazy(() =>
	import(
		'features/Baselines/BaselineComparisonPage/BaselineComparisonPage'
	).then(m => ({default: m.BaselineComparisonPage})),
);
export const SimulationsPage = React.lazy(() =>
	import('features/Simulations/SimulationsPage/SimulationsPage').then(m => ({
		default: m.SimulationsPage,
	})),
);
export const SimulationDetailsPage = React.lazy(() =>
	import('features/Simulations/SimulationDetailsPage/SimulationDetailsPage').then(
		m => ({default: m.SimulationDetailsPage}),
	),
);
export const ReportsPage = React.lazy(() =>
	import('features/Reports/ReportsPage').then(m => ({default: m.ReportsPage})),
);
export const SearchPage = React.lazy(() =>
	import('features/Search/SearchPage').then(m => ({default: m.SearchPage})),
);
export const GetexRegulationPage = React.lazy(
	() => import('features/GetexRegulation/GetexRegulationPage'),
);
export const GetexAsRegDocDetailsPage = React.lazy(
	() => import('features/GetexRegulation/GetexAsRegDocDetailsPage'),
);

// Admin pages — each loaded individually to avoid pulling the entire admin
// section for non-admin users, and to allow per-page chunk splitting.
export const AdminMarketsPage = React.lazy(
	() => import('features/AdminSection/AdminMarketsPage'),
);
export const AdminRegulationsClustersPage = React.lazy(
	() =>
		import(
			'features/AdminSection/AdminRegulationsClustersPage/AdminRegulationsClustersPage'
		),
);
export const AdminVehicleCategoriesPage = React.lazy(
	() => import('features/AdminSection/AdminVehicleCategoriesPage'),
);
export const AdminDriveVariantsPage = React.lazy(
	() => import('features/AdminSection/AdminDriveVariantsPage'),
);
export const AdminEngineVariantsPage = React.lazy(
	() => import('features/AdminSection/AdminEngineVariantsPage'),
);
export const AdminGearboxVariantsPage = React.lazy(
	() => import('features/AdminSection/AdminGearboxVariantsPage'),
);
export const AdminBodyworkVariantsPage = React.lazy(
	() => import('features/AdminSection/AdminBodyworkVariantsPage'),
);
export const AdminActiveTypeMarketsPage = React.lazy(
	() => import('features/AdminSection/AdminActiveTypeMarketsPage'),
);
export const AdminCategoriesPage = React.lazy(
	() => import('features/AdminSection/AdminCategoriesPage'),
);
export const AdminKeywordsPage = React.lazy(
	() => import('features/AdminSection/AdminKeywordsPage'),
);
export const AdminVexClustersPage = React.lazy(
	() => import('features/AdminSection/AdminVexClustersPage'),
);
export const AdminSystemLevelsPage = React.lazy(
	() => import('features/AdminSection/AdminSystemLevelsPage'),
);
export const AdminTagsPage = React.lazy(
	() => import('features/AdminSection/AdminTagsPage'),
);
export const AdminPcmsClustersPage = React.lazy(
	() => import('features/AdminSection/AdminPcmsClustersPage'),
);
export const AdminAttachmentCategoriesPage = React.lazy(
	() => import('features/AdminSection/AdminAttachmentCategoriesPage'),
);
export const AdminDataProtectionPage = React.lazy(
	() => import('features/AdminSection/AdminDataProtectionPage'),
);
export const AdminReferencePage = React.lazy(
	() => import('features/AdminSection/AdminReferencePage'),
);
export const AdminDocumentSourcesPage = React.lazy(
	() => import('features/AdminSection/AdminDocumentSourcesPage'),
);
export const AdminCountriesPage = React.lazy(
	() => import('features/AdminSection/AdminCountriesPage'),
);
export const AdminNotificationPage = React.lazy(
	() => import('features/AdminSection/AdminNotificationPage'),
);
export const AdminPermissionsPage = React.lazy(
	() => import('features/AdminSection/AdminPermissionsPage'),
);
export const AdminGlobalAnnouncementPage = React.lazy(
	() => import('features/AdminSection/AdminGlobalAnnouncementPage'),
);
export const DistributionListSubscriptionsPage = React.lazy(
	() =>
		import(
			'features/DistributionListSubscriptions/DistributionListSubscriptionsPage'
		),
);
export const AdminVkoPage = React.lazy(
	() => import('features/AdminSection/AdminVkoPage'),
);
